// Shared readers for presenting an ASPin policy record.
//
// Two things this exists to fix, both from the OMK individual retest:
//
//  * Item 5 — screens read `policy.additional_details` alone, but that blob is a
//    backend whitelist and several captured fields never reach it. The same
//    policy carries a second, sometimes richer blob on its quotation (an amended
//    policy's snapshot lands there), plus a `quotation_data` sub-blob at either
//    level, plus per-member `additional_details` on the schedule entry. Reading
//    one of five places and printing "N/A" was under-reporting what the customer
//    actually gave us.
//
//  * Item 8 — OMK's In_Arrears/Non_Payment statuses read on screen as
//    "Grace"/"Lapsed" respectively (a direct status mapping, not derived from
//    due-date arithmetic).
//
// No optional chaining / ES6+ syntax the project's Babel config rejects.

import { getFullName } from './personDetails.es6'

// Keys that can identify which member a policy-level record belongs to.
const MEMBER_ID_FIELDS = [
  'national_id',
  'id_number',
  'identification_number',
  'passport',
  'passport_number',
  'guid',
  'dependant_guid'
]

// Lists on a policy blob that hold one record per member.
const MEMBER_LIST_FIELDS = [
  'dependants',
  'dependents',
  'members',
  'lives',
  'family_members',
  'insured_members'
]

function isObject(value) {
  return !!value && typeof value === 'object'
}

/**
 * Every "additional details" blob on a policy, most specific first.
 *
 * Order matters: the policy's own blob wins over the quotation's, and the
 * principal's per-member blob is consulted last so a policy-level value is never
 * overridden by a member-level one.
 */
export function policyDetailSources(policy) {
  if (!isObject(policy)) {
    return []
  }
  const sources = []
  const push = blob => {
    if (isObject(blob)) {
      sources.push(blob)
    }
  }

  const policyAd = policy.additional_details
  push(policyAd)
  if (isObject(policyAd)) {
    push(policyAd.quotation_data)
  }

  const quotation = policy.quotation
  if (isObject(quotation)) {
    const quotationAd = quotation.additional_details
    push(quotationAd)
    if (isObject(quotationAd)) {
      push(quotationAd.quotation_data)
    }
  }

  const principal = principalDependant(policy)
  if (isObject(principal)) {
    push(principal.additional_details)
  }

  return sources
}

/** The schedule entry for the principal member, or null. */
export function principalScheduleEntry(policy) {
  const quotation = isObject(policy) ? policy.quotation : null
  const entries =
    isObject(quotation) && Array.isArray(quotation.policy_schedule_entries)
      ? quotation.policy_schedule_entries
      : []
  for (let i = 0; i < entries.length; i += 1) {
    const entry = entries[i]
    const dependant = isObject(entry) ? entry.dependant : null
    if (
      isObject(dependant) &&
      /principal/i.test(String(dependant.relationship))
    ) {
      return entry
    }
  }
  return null
}

/** The principal's dependant record, or null. */
export function principalDependant(policy) {
  const entry = principalScheduleEntry(policy)
  return isObject(entry) && isObject(entry.dependant) ? entry.dependant : null
}

/**
 * The first non-empty value for `keys` across every blob on the policy.
 *
 * `keys` may be a single key or a list of aliases (the journeys and the engine
 * do not always agree on a name — `phone_number` vs `msisdn`).
 */
export function readPolicyField(policy, keys) {
  const names = Array.isArray(keys) ? keys : [keys]
  const sources = policyDetailSources(policy)
  for (let s = 0; s < sources.length; s += 1) {
    for (let k = 0; k < names.length; k += 1) {
      const value = sources[s][names[k]]
      if (value === 0 || value === false) {
        return value
      }
      if (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ''
      ) {
        return value
      }
    }
  }
  return undefined
}

function isSameMember(record, person) {
  if (!isObject(record) || !isObject(person)) {
    return false
  }
  for (let i = 0; i < MEMBER_ID_FIELDS.length; i += 1) {
    const field = MEMBER_ID_FIELDS[i]
    const left = record[field]
    const right = person[field]
    if (left && right && String(left) === String(right)) {
      return true
    }
  }
  const recordName = getFullName(record)
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
  const personName = getFullName(person)
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
  return !!recordName && recordName === personName
}

/**
 * Read one field for a member, looking at the member record itself, its nested
 * detail blobs, and then any policy-level list that names the same person.
 *
 * Used for the details the engine drops off the dependant record (gender, phone)
 * so anything that DID survive somewhere is shown instead of "N/A" (item 4).
 */
export function readMemberField(policy, member, keys) {
  const names = Array.isArray(keys) ? keys : [keys]
  if (!isObject(member)) {
    return undefined
  }
  const direct = [member, member.additional_details, member.details]
  for (let s = 0; s < direct.length; s += 1) {
    if (!isObject(direct[s])) {
      continue
    }
    for (let k = 0; k < names.length; k += 1) {
      const value = direct[s][names[k]]
      if (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ''
      ) {
        return value
      }
    }
  }

  const sources = policyDetailSources(policy)
  for (let s = 0; s < sources.length; s += 1) {
    for (let l = 0; l < MEMBER_LIST_FIELDS.length; l += 1) {
      const list = sources[s][MEMBER_LIST_FIELDS[l]]
      if (!Array.isArray(list)) {
        continue
      }
      for (let r = 0; r < list.length; r += 1) {
        if (!isSameMember(list[r], member)) {
          continue
        }
        for (let k = 0; k < names.length; k += 1) {
          const value = list[r][names[k]]
          if (
            value !== null &&
            value !== undefined &&
            String(value).trim() !== ''
          ) {
            return value
          }
        }
      }
    }
  }
  return undefined
}

/**
 * How a policy's status should read on screen.
 *
 * OMK reads In_Arrears as "Grace" and Non_Payment as "Lapsed" — a direct
 * status mapping (retest item 8 previously derived this from due-date
 * arithmetic; now it just trusts the engine's status). Every other status is
 * passed through untouched.
 */
export function policyStatusLabel(policy) {
  const status = isObject(policy) ? String(policy.status || '') : ''
  if (status === 'In_Arrears') {
    return { label: 'Grace' }
  }
  if (status === 'Non_Payment') {
    return { label: 'Lapsed' }
  }
  return { label: status || 'N/A' }
}
